import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { CONFIG } from '../config';
import {
  DataRange,
  DayKey,
  LoadedSpan,
  PickResolution,
  buildMonthGrid,
  dayKeyOf,
  enabledDayBounds,
  formatDayLabel,
  initialMonth,
  isDayEnabled,
  monthLabel,
  resolvePick,
  shiftMonth,
} from '../utils/goToDate';
import './GoToDatePopup.css';

/**
 * The "go to date" calendar, opened by long-pressing the chart's time axis.
 *
 * The whole point of this component is that it refuses to guess. It asks the
 * engine for the real tick coverage of the symbol's `.scid` and enables only
 * days inside it. When the engine has nothing to report — no file, an empty
 * file, an unknown symbol, or an unreachable backend — every day renders
 * disabled and the footer says why.
 *
 * That last part is the design decision worth defending: the obvious fallback
 * of "enable whatever the loaded candles cover" would light up a handful of
 * days that the user can already reach by dragging, while implying the rest of
 * history does not exist. And "today minus N days" would light up days that
 * jump nowhere. A picker that offers a jump must be able to make it.
 */

/**
 * How the range request came back. Four outcomes, four different sentences —
 * "no data" and "couldn't ask" are not the same thing to someone deciding
 * whether to retry.
 */
export type RangeState =
  | { status: 'loading' }
  | { status: 'ok'; range: DataRange; count: number }
  | { status: 'empty' }
  | { status: 'missing' }
  | { status: 'error'; message: string };

/**
 * Progress of the history paging kicked off by picking an unloaded date. Owned
 * by ChartView (it drives the socket) and rendered here so the feedback stays
 * where the user's attention is.
 */
export interface GoToDateFetchState {
  day: DayKey;
  pages: number;
  status: 'fetching' | 'exhausted' | 'capped' | 'stalled';
}

interface GoToDatePopupProps {
  symbol: string;
  /** Client coordinates of the press that opened this. */
  anchor: { x: number; y: number };
  /** Time at the right edge of the current view, so the calendar opens where the user is. */
  focusMs: number | null;
  /** Span of the loaded candle series, used to decide scroll-vs-fetch. */
  loadedSpan: LoadedSpan | null;
  fetchState: GoToDateFetchState | null;
  onPick: (resolution: PickResolution, day: DayKey) => void;
  onClose: () => void;
}

const WEEKDAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/** Keeps the panel off the viewport edges once it is anchored to a press point. */
const VIEWPORT_MARGIN_PX = 8;
/** Gap between the press point and the panel, so a finger does not cover it. */
const ANCHOR_GAP_PX = 12;

const GoToDatePopup: React.FC<GoToDatePopupProps> = ({
  symbol,
  anchor,
  focusMs,
  loadedSpan,
  fetchState,
  onPick,
  onClose,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [rangeState, setRangeState] = useState<RangeState>({ status: 'loading' });
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const [month, setMonth] = useState(() => initialMonth(null, focusMs));
  /** The month only auto-follows the range once; after that the user drives it. */
  const monthPinnedRef = useRef(false);

  const range = rangeState.status === 'ok' ? rangeState.range : null;

  // --- range request -------------------------------------------------------
  // Fires on open rather than on mount of the chart: no request at all until
  // somebody actually long-presses the axis.
  useEffect(() => {
    const controller = new AbortController();
    setRangeState({ status: 'loading' });

    const url = `${CONFIG.getBackendUrl()}/api/candles/range?symbol=${encodeURIComponent(symbol)}`;

    fetch(url, { signal: controller.signal })
      .then(async (res) => {
        // 404 is the engine's honest "I have no .scid for this symbol, or it is
        // unreadable". Not an error to apologise for, but nothing is selectable.
        if (res.status === 404) {
          setRangeState({ status: 'missing' });
          return;
        }
        if (!res.ok) {
          setRangeState({ status: 'error', message: `engine returned ${res.status}` });
          return;
        }
        const body = (await res.json()) as {
          first: number | null;
          last: number | null;
          count: number;
        };
        const first = body?.first;
        const last = body?.last;
        // The documented empty-file case: 200 with null bounds and count 0. A
        // count of 0 with non-null bounds would be contradictory, so treat any
        // of the three as "nothing there" rather than trusting half of it.
        if (
          first == null ||
          last == null ||
          !Number.isFinite(first) ||
          !Number.isFinite(last) ||
          !body.count
        ) {
          setRangeState({ status: 'empty' });
          return;
        }
        setRangeState({ status: 'ok', range: { first, last }, count: body.count });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setRangeState({
          status: 'error',
          message: err instanceof Error ? err.message : 'request failed',
        });
      });

    return () => controller.abort();
  }, [symbol]);

  // Once the range lands, jump to a month that actually has data — but only if
  // the user has not already paged somewhere themselves.
  useEffect(() => {
    if (!range || monthPinnedRef.current) return;
    setMonth(initialMonth(range, focusMs));
  }, [range, focusMs]);

  // --- placement -----------------------------------------------------------
  // Anchored above the press point because the time axis is at the bottom of
  // the chart; measured after render so the clamp uses the real panel size.
  useLayoutEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN_PX;
    const left = Math.max(VIEWPORT_MARGIN_PX, Math.min(anchor.x - width / 2, maxLeft));
    const above = anchor.y - height - ANCHOR_GAP_PX;
    // If there is no room above (a very short chart), fall below instead of
    // letting the panel run off the top where the header would be unreachable.
    const top =
      above >= VIEWPORT_MARGIN_PX
        ? above
        : Math.min(anchor.y + ANCHOR_GAP_PX, window.innerHeight - height - VIEWPORT_MARGIN_PX);
    setPosition({ left, top: Math.max(VIEWPORT_MARGIN_PX, top) });
  }, [anchor.x, anchor.y, rangeState.status, fetchState?.status]);

  // --- dismissal -----------------------------------------------------------
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    const onPointerDown = (e: PointerEvent) => {
      if (panelRef.current?.contains(e.target as Node)) return;
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    // Capture phase so the chart's own pointer handling does not get to treat
    // the dismissing press as the start of a pan.
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [onClose]);

  // --- derived -------------------------------------------------------------
  const grid = useMemo(() => buildMonthGrid(month.year, month.month), [month]);
  const bounds = useMemo(() => enabledDayBounds(range), [range]);
  const todayKey = useMemo(() => dayKeyOf(Date.now()), []);
  const loadedFirstDay = useMemo(
    () => (loadedSpan ? dayKeyOf(loadedSpan.first) : null),
    [loadedSpan]
  );

  const canStep = useCallback(
    (delta: number) => {
      const next = shiftMonth(month.year, month.month, delta);
      if (!range) return false;
      // Allow stepping only while there is still covered data in that
      // direction; the arrow greys out at the ends of history.
      if (!bounds) return false;
      const target = delta < 0 ? bounds.first : bounds.last;
      const targetMonth = Number(target.slice(0, 4)) * 12 + Number(target.slice(5, 7));
      const nextMonth = next.year * 12 + next.month;
      return delta < 0 ? nextMonth >= targetMonth : nextMonth <= targetMonth;
    },
    [month, range, bounds]
  );

  const step = useCallback(
    (delta: number) => {
      monthPinnedRef.current = true;
      setMonth((m) => shiftMonth(m.year, m.month, delta));
    },
    []
  );

  const handlePick = useCallback(
    (day: DayKey) => {
      if (!range || !isDayEnabled(day, range)) return;
      onPick(resolvePick(day, range, loadedSpan), day);
    },
    [range, loadedSpan, onPick]
  );

  // --- status copy ---------------------------------------------------------
  const statusLine = (() => {
    if (fetchState) {
      switch (fetchState.status) {
        case 'fetching':
          return {
            tone: 'busy' as const,
            text: `loading history back to ${formatDayLabel(fetchState.day)}… (${fetchState.pages} ${fetchState.pages === 1 ? 'page' : 'pages'})`,
          };
        case 'exhausted':
          return {
            tone: 'warn' as const,
            text: `history stops before ${formatDayLabel(fetchState.day)} — the engine has no older bars to send. Staying put.`,
          };
        case 'capped':
          return {
            tone: 'warn' as const,
            text: `gave up after ${fetchState.pages} pages without reaching ${formatDayLabel(fetchState.day)}. Staying put rather than landing somewhere else.`,
          };
        case 'stalled':
          return {
            tone: 'warn' as const,
            text: `history request timed out before reaching ${formatDayLabel(fetchState.day)}. Staying put.`,
          };
      }
    }
    switch (rangeState.status) {
      case 'loading':
        return { tone: 'busy' as const, text: 'checking what data exists…' };
      case 'missing':
        return { tone: 'warn' as const, text: `no data file for ${symbol} — nothing to jump to.` };
      case 'empty':
        return { tone: 'warn' as const, text: `${symbol} has a data file but no ticks in it.` };
      case 'error':
        return {
          tone: 'warn' as const,
          text: `couldn't read the available range (${rangeState.message}). Nothing enabled rather than guessing.`,
        };
      case 'ok':
        return {
          tone: 'info' as const,
          text: `data from ${formatDayLabel(bounds!.first)} to ${formatDayLabel(bounds!.last)}`,
        };
    }
  })();

  const busy = fetchState?.status === 'fetching';

  return (
    <div
      className="goto-date-popup"
      ref={panelRef}
      role="dialog"
      aria-label="Go to date"
      style={{
        left: position?.left ?? anchor.x,
        top: position?.top ?? anchor.y,
        // Hidden until measured, so it never flashes at the un-clamped spot.
        visibility: position ? 'visible' : 'hidden',
      }}
      // The chart listens on the container for its own gestures; keep presses
      // inside the panel from reaching it.
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="goto-date-header">
        <button
          type="button"
          className="goto-date-nav"
          onClick={() => step(-1)}
          disabled={!canStep(-1)}
          aria-label="Previous month"
          title="Previous month"
        >
          <ChevronLeft size={14} />
        </button>
        <span className="goto-date-month">{monthLabel(month.year, month.month)}</span>
        <button
          type="button"
          className="goto-date-nav"
          onClick={() => step(1)}
          disabled={!canStep(1)}
          aria-label="Next month"
          title="Next month"
        >
          <ChevronRight size={14} />
        </button>
        <button
          type="button"
          className="goto-date-close"
          onClick={onClose}
          aria-label="Close"
          title="Close"
        >
          <X size={14} />
        </button>
      </div>

      <div className="goto-date-weekdays" aria-hidden="true">
        {WEEKDAY_INITIALS.map((d, i) => (
          <span key={`${d}-${i}`}>{d}</span>
        ))}
      </div>

      <div className="goto-date-grid" role="grid">
        {grid.map((day, i) => {
          if (day === null) return <span key={`pad-${i}`} className="goto-date-cell is-pad" />;
          const enabled = !busy && isDayEnabled(day, range);
          const loaded = loadedFirstDay != null && day >= loadedFirstDay;
          return (
            <button
              type="button"
              key={day}
              className={[
                'goto-date-cell',
                enabled ? 'is-enabled' : 'is-disabled',
                loaded && enabled ? 'is-loaded' : '',
                day === todayKey ? 'is-today' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              disabled={!enabled}
              aria-disabled={!enabled}
              onClick={() => handlePick(day)}
              title={
                enabled
                  ? loaded
                    ? `${formatDayLabel(day, 'long')} — already loaded`
                    : `${formatDayLabel(day, 'long')} — will load history first`
                  : `${formatDayLabel(day, 'long')} — no data`
              }
            >
              {Number(day.slice(8))}
            </button>
          );
        })}
        {!range && rangeState.status !== 'loading' && (
          <div className="goto-date-empty-veil">nothing to jump to</div>
        )}
      </div>

      <div className={`goto-date-status is-${statusLine.tone}`}>{statusLine.text}</div>
    </div>
  );
};

export default GoToDatePopup;
